/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { View } from "react-native";
import AnimatedLottieView from "lottie-react";

import { Text } from "UpduoLibrary";
import { formattedDuration, isNil, monthArray } from "config/Utilities";
import ManagerDashboardWidget from "./ManagerDashboardWidget";
import progressSpinner from "assets/quota_progress_lottie";
import { upduoFailure, upduoSuccess } from "config/UpduoColors";

export default function TrackerWidget({
  quotaTarget,
  quotaProgress,
  isOnTrack,
  lastUpdated,
}: {
  quotaTarget?: number | null;
  quotaProgress?: number | null;
  targetDate: Date;
  isOnTrack?: boolean | null;
  lastUpdated?: Date;
}) {
  if (isNil(quotaTarget) || isNil(quotaProgress) || isNil(lastUpdated)) {
    return (
      <ManagerDashboardWidget title={"tracker"}>
        <Text style={{ textAlign: "center" }}>
          {"Quota progress has not been calculated yet for this organization"}
        </Text>
      </ManagerDashboardWidget>
    );
  }

  const now = new Date();
  const quotaCompletion = quotaTarget === 0 ? 1 : quotaProgress / quotaTarget;
  const lottieFile =
    progressSpinner[Math.floor(Math.min(10, quotaCompletion * 10))];

  const currentMonth = monthArray[now.getMonth()];

  const secondsSinceLastUpdated =
    (now.getTime() - lastUpdated.getTime()) / 1000;

  const onTrackTag = isNil(isOnTrack) ? null : (
    <Text
      style={{
        borderColor: isOnTrack ? upduoSuccess : upduoFailure,
        borderStyle: "solid",
        borderWidth: 1,
        borderRadius: 4,
        paddingVertical: 4,
        paddingHorizontal: 10,
        textTransform: "uppercase",
        color: isOnTrack ? upduoSuccess : upduoFailure,
        fontSize: 10,
      }}
    >
      {isOnTrack ? "on track" : "not on track"}
    </Text>
  );

  return (
    <ManagerDashboardWidget title={"tracker"} tag={onTrackTag}>
      <View style={{ alignItems: "center" }}>
        <AnimatedLottieView
          style={{ width: 140 }}
          animationData={lottieFile}
          autoPlay={true}
          loop={false}
        />
        <View
          style={{
            position: "absolute",
            display: "flex",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text isHeading={true} style={{ fontSize: 32 }}>
            {quotaTarget
              ? `${Math.min(Math.floor(quotaCompletion * 100), 100)}%`
              : "N/A"}
          </Text>
          <Text style={{ fontSize: 10 }}>{"towards quota"}</Text>
        </View>
      </View>
      <View style={{ flexDirection: "row" }}>
        <Text isHeading={true}>{`${currentMonth} Quota: `}</Text>
        <Text>{`${quotaProgress}/${quotaTarget} sessions`}</Text>
      </View>
      <View style={{ flexDirection: "row" }}>
        <Text isHeading={true}>{"Last updated: "}</Text>
        <Text>
          {secondsSinceLastUpdated > 0
            ? `${formattedDuration(secondsSinceLastUpdated).join(" ")} ago`
            : "just now"}
        </Text>
      </View>
    </ManagerDashboardWidget>
  );
}
